import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@woodmac-hycb/theme-provider';
import {
    getWoodmacTheme,
    getLensTheme,
    getDSTestTheme,
    SemanticTokenBasedTheme,
} from '@woodmac-hycb/theme-default';
import {
    HostThemeContext,
    isKnownTheme,
    useHostTheme,
    type HostThemeName,
} from './context';

export { useHostTheme };
export type { HostThemeName };

/**
 * Single source of truth for the host design-system theme.
 *
 * The host wraps the entire application (and, via the host `Portal`, every
 * guest bundle mounted inline in the same React tree) in the HYCB
 * `ThemeProvider` this component renders. A guest bundle inherits (and follows
 * switches of) this theme ONLY when it both externalises
 * `@woodmac-hycb/theme-provider` (sharing the host's context singleton) and
 * consumes the theme via `useTheme()`. Bundles/components that hardcode their
 * own `getWoodmacTheme()` / `getTheme()` bake a fixed theme and are unaffected
 * by switches here. As of writing, discovery and UVV are migrated to consume
 * the host theme; other bundles are not, so a switch may only update part of
 * the page until they are migrated too.
 *
 * Theme objects are memoised per name so their identity is stable across
 * renders. `@woodmac-hycb/theme-provider` re-injects its `:root` variables
 * whenever the theme object identity changes, so a stable identity avoids
 * needless re-injection while still updating correctly on a real switch.
 */

const THEME_GETTERS: Record<HostThemeName, () => ReturnType<typeof getWoodmacTheme>> = {
    [SemanticTokenBasedTheme.Woodmac]: getWoodmacTheme,
    [SemanticTokenBasedTheme.Lens]: getLensTheme,
    [SemanticTokenBasedTheme.DsTestMode]: getDSTestTheme,
};

interface HostThemeProviderProps {
    children: ReactNode;
    initialTheme?: HostThemeName;
}

const HostThemeProvider: FC<HostThemeProviderProps> = ({
    children,
    initialTheme = SemanticTokenBasedTheme.Woodmac,
}) => {
    const [themeName, setThemeNameState] = useState<HostThemeName>(initialTheme);

    // Explicit guard: ignore unknown values and no-op when the theme is
    // unchanged, so repeated calls (e.g. a UI control or console) can't trigger
    // a needless re-render / :root re-injection on the same theme.
    const setThemeName = useCallback((name: HostThemeName) => {
        if (!isKnownTheme(name)) {
            return;
        }
        setThemeNameState((current) => (current === name ? current : name));
    }, []);

    // Stable theme identity per name — recomputed only when the name changes.
    const theme = useMemo(() => THEME_GETTERS[themeName](), [themeName]);

    const contextValue = useMemo(
        () => ({ themeName, setThemeName }),
        [themeName, setThemeName],
    );

    // Expose a switch on window so the theme can be toggled at the host level
    // (e.g. from an app-level control or, for now, the console) and cascade to
    // all bundles. Kept intentionally small — a UI control can call
    // `useHostTheme().setThemeName` instead.
    //
    // Dev-only for now: the global is registered only outside production so we
    // don't ship a console-reachable theme switch to prod users. Later we may
    // want to expose it more widely (e.g. behind a flag); if so, relax this
    // guard. `NODE_ENV` is the signal that works across both webpack configs
    // (prod build defines it; dev server leaves it undefined → treated as dev).
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            return undefined;
        }
        window.__setLensHostTheme = setThemeName;
        return () => {
            delete window.__setLensHostTheme;
        };
    }, [setThemeName]);

    return (
        <HostThemeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                {children}
            </ThemeProvider>
        </HostThemeContext.Provider>
    );
};

export default HostThemeProvider;
